import posthog from 'posthog-js'
import {
  readWebSurveyInstrumentId,
  readWebSurveyOnboardingSession,
  readWebSurveySurveyFirst,
} from '@/features/auth/lib/constants'
import { useSessionStore } from '@/stores/useSessionStore'
import { captureAnalyticsEvent, isPostHogEnabled } from './posthog'

/** BFP-1124 — Responder Web sign-up survey funnel. */
export const RESPONDER_FUNNEL_NAME = 'responder_web_signup' as const

export const ResponderFunnelEvent = {
  WELCOME_VIEWED: 'welcome_viewed',
  SURVEY_STARTED: 'survey_started',
  SURVEY_SUBMITTED: 'survey_submitted',
  MOBILE_VERIFIED: 'mobile_verified',
  ACCOUNT_CREATION_STARTED: 'account_creation_started',
  ACCOUNT_CREATED: 'account_created',
  SIGNUP_SURVEY_COMPLETED: 'signup_survey_completed',
  REDEEM_SCREEN_VIEWED: 'redeem_screen_viewed',
  REWARD_REDEEMED: 'reward_redeemed',
} as const

export type ResponderFunnelEventName =
  (typeof ResponderFunnelEvent)[keyof typeof ResponderFunnelEvent]

export type DeviceType = 'mobile' | 'tablet' | 'desktop'
export type RedeemScreenEntry = 'signup_survey' | 'direct'
export type SignupCompletionSource = 'account_created' | 'claim_pending'

type FunnelPropValue = string | number | boolean | null

export interface FunnelContext {
  surveyId?: string | null
  campaignId?: string | null
  responseId?: string | null
  rewardAmount?: number | null
}

/**
 * Only these keys can reach PostHog. Mobile number, email, password and OTP
 * must never be sent (BFP-1124 privacy rule).
 */
const ALLOWED_FUNNEL_KEYS = new Set<string>([
  'anonymous_id',
  'responder_id',
  'session_id',
  'survey_id',
  'campaign_id',
  'response_id',
  'reward_amount',
  'device_type',
  'timestamp',
  'funnel',
  'is_signup_survey',
  'survey_name',
  'entry',
  'completion_source',
  'brand_name',
  'product_id',
  'amount_usd',
])

const VIEW_DEDUPE_PREFIX = 'ph_funnel_'

export function detectDeviceType(): DeviceType {
  const ua = globalThis.navigator?.userAgent ?? ''
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/i.test(ua)) return 'tablet'
  if (/mobi|iphone|ipod|android/i.test(ua)) return 'mobile'
  return 'desktop'
}

function readAnonymousId(): string | null {
  try {
    const id = posthog.get_distinct_id?.()
    return id ? String(id) : null
  } catch {
    return null
  }
}

/** Real Responder id only — guest / pending session principals are not Responders. */
function readResponderId(): string | null {
  const user = useSessionStore.getState().user
  if (!user?.id || user.guest || user.surveyPending) return null
  if (user.id.includes(':')) return null
  return user.id
}

export function pickAllowedFunnelProps(
  props: Record<string, FunnelPropValue | undefined>,
): Record<string, FunnelPropValue> {
  const out: Record<string, FunnelPropValue> = {}
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === '') continue
    if (!ALLOWED_FUNNEL_KEYS.has(key)) continue
    out[key] = value
  }
  return out
}

export function buildFunnelBaseProps(ctx: FunnelContext = {}): Record<string, FunnelPropValue> {
  return pickAllowedFunnelProps({
    anonymous_id: readAnonymousId(),
    responder_id: readResponderId(),
    session_id: readWebSurveyOnboardingSession(),
    survey_id: ctx.surveyId ?? readWebSurveyInstrumentId(),
    campaign_id: ctx.campaignId,
    response_id: ctx.responseId,
    reward_amount: typeof ctx.rewardAmount === 'number' ? ctx.rewardAmount : undefined,
    device_type: detectDeviceType(),
    timestamp: new Date().toISOString(),
    funnel: RESPONDER_FUNNEL_NAME,
    is_signup_survey: readWebSurveySurveyFirst(),
  })
}

function captureFunnelEvent(
  event: ResponderFunnelEventName,
  ctx: FunnelContext,
  extra: Record<string, FunnelPropValue | undefined> = {},
): void {
  if (!isPostHogEnabled()) return
  captureAnalyticsEvent(event, {
    ...buildFunnelBaseProps(ctx),
    ...pickAllowedFunnelProps(extra),
  })
}

/** One view capture per key per tab session. */
export function shouldCaptureFunnelView(event: ResponderFunnelEventName, key = ''): boolean {
  if (globalThis.sessionStorage === undefined) return true
  const storageKey = `${VIEW_DEDUPE_PREFIX}${event}_${key}`
  try {
    if (globalThis.sessionStorage.getItem(storageKey)) return false
    globalThis.sessionStorage.setItem(storageKey, '1')
    return true
  } catch {
    return true
  }
}

export function captureWelcomeViewed(ctx: FunnelContext & { surveyId: string }): void {
  if (!isPostHogEnabled()) return
  if (!shouldCaptureFunnelView(ResponderFunnelEvent.WELCOME_VIEWED, ctx.surveyId)) return
  captureFunnelEvent(ResponderFunnelEvent.WELCOME_VIEWED, ctx)
}

export function captureSurveyStarted(ctx: FunnelContext & { surveyName?: string | null }): void {
  captureFunnelEvent(ResponderFunnelEvent.SURVEY_STARTED, ctx, {
    survey_name: ctx.surveyName ?? undefined,
  })
}

/** Survey answers submitted — NOT Sign-Up Survey completion. */
export function captureSurveySubmitted(ctx: FunnelContext): void {
  captureFunnelEvent(ResponderFunnelEvent.SURVEY_SUBMITTED, ctx)
}

export function captureMobileVerified(ctx: FunnelContext = {}): void {
  captureFunnelEvent(ResponderFunnelEvent.MOBILE_VERIFIED, ctx)
}

export function captureAccountCreationStarted(ctx: FunnelContext = {}): void {
  if (!isPostHogEnabled()) return
  const key = readWebSurveyOnboardingSession() ?? ''
  if (!shouldCaptureFunnelView(ResponderFunnelEvent.ACCOUNT_CREATION_STARTED, key)) return
  captureFunnelEvent(ResponderFunnelEvent.ACCOUNT_CREATION_STARTED, ctx)
}

export function captureAccountCreated(ctx: FunnelContext = {}): void {
  captureFunnelEvent(ResponderFunnelEvent.ACCOUNT_CREATED, ctx)
}

/**
 * Fires only once the survey response is linked to the Responder
 * (`surveyLinked === true` from identity).
 */
export function captureSignupSurveyCompleted(
  ctx: FunnelContext & { surveyLinked?: boolean; source: SignupCompletionSource },
): void {
  if (ctx.surveyLinked !== true) return
  captureFunnelEvent(ResponderFunnelEvent.SIGNUP_SURVEY_COMPLETED, ctx, {
    completion_source: ctx.source,
  })
}

export function captureRedeemScreenViewed(entry: RedeemScreenEntry): void {
  if (!isPostHogEnabled()) return
  if (!shouldCaptureFunnelView(ResponderFunnelEvent.REDEEM_SCREEN_VIEWED, entry)) return
  captureFunnelEvent(ResponderFunnelEvent.REDEEM_SCREEN_VIEWED, {}, { entry })
}

export function captureRewardRedeemed(props: {
  brandName: string
  productId: string
  amountUsd: number
}): void {
  captureFunnelEvent(
    ResponderFunnelEvent.REWARD_REDEEMED,
    { rewardAmount: props.amountUsd },
    {
      brand_name: props.brandName,
      product_id: props.productId,
      amount_usd: props.amountUsd,
    },
  )
}
